import * as React from 'react';
import { useTheme, darken } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import { useTranslation } from 'react-i18next';
import { secureRemoveItem } from '@utils';

import { useSession, useConnection } from '@hooks';
import { useDeleteUserMutation } from '@graphql';

interface DangerZoneProps {
  handleCloseDialog: () => void;
  handleCloseDrawer: () => void;
}

type Action = 'disable' | 'delete';

export function DangerZone(props: Readonly<DangerZoneProps>) {
  const { handleCloseDialog, handleCloseDrawer } = props;
  const [confirmed, setConfirmed] = React.useState<Action | null>(null);
  const { logout } = useConnection();
  const { user } = useSession();
  const theme = useTheme();
  const { t } = useTranslation('account');

  const [deleteUser] = useDeleteUserMutation();

  React.useEffect(() => {
    if (confirmed === 'delete') {
      handleCloseDialog();
      handleCloseDrawer();
    }
    const deleteAction = async () => {
      await deleteUser();
      await secureRemoveItem('accessToken');
      await secureRemoveItem('refreshToken');
      await logout();
    };

    return () => {
      if (confirmed === 'delete') {
        deleteAction();
      }
    };
  }, [confirmed]);

  if (!user) {
    return null;
  }

  return (
    <Stack width="100%" spacing={2}>
      <Typography fontWeight="bold">{t('danger zone.title')}</Typography>
      <Stack
        width="100%"
        sx={{
          border: '1px solid',
          borderColor: theme.palette.error.main,
          borderRadius: 2,
          backgroundColor: darken(theme.palette.error.dark, 0.6),
        }}
      >
        {/* <Stack
          p={1}
          width="100%"
          direction="row"
          justifyContent="space-between"
          sx={{
            borderBottom: '1px solid',
            borderColor: theme.palette.error.main,
          }}
        >
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('danger zone.Disable my account')}
            </Typography>
            <Typography variant="caption">
              {t('danger zone.The account will not be reactivatable for 24 hours.')}
            </Typography>
          </Stack>
          <Stack justifyContent="center" alignItems="center">
            <AlertDialog action="disable" />
          </Stack>
        </Stack> */}
        <Stack p={1} spacing={1} width="100%" direction="row" justifyContent="space-between">
          <Stack spacing={1}>
            <Typography variant="body2" fontWeight="bold">
              {t('danger zone.Delete my account')}
            </Typography>
            <Typography variant="caption">
              {t('danger zone.The final deletion will take place in 30 days.')}
            </Typography>
          </Stack>
          <Stack justifyContent="center" alignItems="center">
            <AlertDialog action="delete" setConfirmed={setConfirmed} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

interface AlertDialogProps {
  action: Action;
  setConfirmed: (action: Action) => void;
}

function AlertDialog(props: Readonly<AlertDialogProps>) {
  const { action, setConfirmed } = props;
  const [open, setOpen] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [deleteCheck, setDeleteCheck] = React.useState('');
  const [isDisabled, setIsDisabled] = React.useState(true);
  const { t } = useTranslation('account');

  const handleClose = () => {
    setOpen(false);
  };

  const handleDisable = () => {
    if (!disabled) {
      setOpen(true);
    }
  };

  const handleDelete = () => {
    setOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmed(action);
    handleClose();
  };

  const handleCancel = () => {
    handleClose();
  };

  React.useEffect(() => {
    // check by regext test is "delete word confirmation" matches
    // if not, set isDisabled to true
    const regex = new RegExp(`^${t('danger zone.delete word confirmation')}$`, 'i');
    if (deleteCheck && regex.test(deleteCheck.toLowerCase())) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [deleteCheck]);

  return (
    <React.Fragment>
      {action === 'disable' && (
        <Switch color="error" size="small" checked={disabled} onChange={handleDisable} />
      )}
      {action === 'delete' && (
        <Button variant="contained" color="error" size="small" onClick={handleDelete}>
          {t('delete', { ns: 'cta' })}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Stack width="100%" pt={3} pl={3}>
          {action === 'disable' && (
            <Typography variant="h5" color="error.main">
              {t('danger zone.Disable my account')}
            </Typography>
          )}
          {action === 'delete' && (
            <Typography variant="h5" color="error.main">
              {t('danger zone.Delete my account')}
            </Typography>
          )}
        </Stack>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {action === 'disable' && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.primary">
                  {t('danger zone.Deactivating your account will make it invisible to other users.')}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {t(
                    'danger zone.All your current or future reservations will be canceled with no possibility of reversing them.'
                  )}
                </Typography>
              </Stack>
            )}
            {action === 'delete' && (
              <Stack spacing={1}>
                <Typography variant="body2" color="text.primary">
                  {t('danger zone.Deleting your account will make it invisible to other users.')}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {t(
                    'danger zone.All your current or future reservations will be canceled with no possibility of reversing them.'
                  )}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {t(
                    'danger zone.Your account will be permanently deleted in 30 days. After this period, it will no longer be possible to recover it. If you wish to restore it before this time, simply log back in to your account.'
                  )}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {t(
                    'danger zone.By clicking "Confirm", your account will be deleted and you will be immediately logged out.'
                  )}
                </Typography>
                <Divider variant="middle" />
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.primary">
                    {t('danger zone.Type delete word confirmation to confirm', {
                      deleteWordConfirmation: t('danger zone.delete word confirmation'),
                    })}
                  </Typography>

                  <TextField
                    size="small"
                    variant="outlined"
                    enterKeyHint="done"
                    value={deleteCheck}
                    // disabled={called}
                    onChange={(e) => setDeleteCheck(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleConfirm();
                      }
                    }}
                  />
                </Stack>
              </Stack>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirm} variant="outlined" color="error" disabled={isDisabled}>
            {t('confirm', { ns: 'cta' })}
          </Button>
          <Button onClick={handleCancel} autoFocus variant="contained" color="uncolored">
            {t('cancel', { ns: 'cta' })}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
